import React, { KeyboardEvent, useEffect, useState } from 'react';

import { useParams } from 'react-router';

import { Alert, Button, Stack, TextField, Typography } from '@mui/material';
import Grid2 from '@mui/material/Grid2';
import { GridPaginationModel } from '@mui/x-data-grid';

import ActivityLogGrid from '~/components/LeBusinessTraveller/components/ActivityLogGrid';

import { getActivityLog } from '~/services/BusinessTraveller/BusinessTravellerService';

interface Params {
  businessId: string;
  tab: string;
}

export default function ActivityTab() {
  const [activityLog, setActivityLog] = useState<Array<App.BusinessActivityLog>>();
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    pageSize: 10,
    page: 0,
  });
  const [totalActivityLogCount, setTotalActivityLogCount] = useState(0);
  const [isFetching, setIsFetching] = useState(false);
  const [fetchActivityLogError, setFetchActivityLogError] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sort, setSort] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [resource, setResource] = useState('');
  const [operation, setOperation] = useState('');
  const { businessId } = useParams<Params>();

  async function fetchActivityLog() {
    try {
      setIsFetching(true);
      const result = await getActivityLog(businessId, paginationModel, {
        searchQuery: searchQuery || undefined,
        sort: sort || undefined,
        sortDirection: sortDirection || undefined,
        resource: resource || undefined,
        operation: operation || undefined,
      });
      setActivityLog(result.result);
      setTotalActivityLogCount(result.meta.total);
      setFetchActivityLogError(false);
    } catch (e) {
      setFetchActivityLogError(true);
      console.error(e);
    } finally {
      setIsFetching(false);
    }
  }

  useEffect(() => {
    fetchActivityLog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel, sort, sortDirection]);

  async function clearFilters() {
    setSearchQuery('');
    setResource('');
    setOperation('');
    setSort('createdAt');
    setSortDirection('desc');
    setPaginationModel({ pageSize: 10, page: 0 });
  }

  async function filter() {
    setPaginationModel({ pageSize: 10, page: 0 });
  }

  async function handleEnterPress(e: KeyboardEvent<HTMLDivElement>) {
    if (e.key === 'Enter') {
      await filter();
    }
  }

  return (
    <Stack gap={2}>
      {fetchActivityLogError && <Alert severity="error">Error fetching activity log</Alert>}
      <Typography variant="h5">Activity Log</Typography>
      <Stack width="100%" gap={2}>
        <Grid2 container spacing={2}>
          <Grid2
            size={{
              xs: 12,
              md: 6,
            }}
          >
            <TextField
              id="ActivitySearch"
              fullWidth
              label="Search"
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleEnterPress}
              placeholder="Enter keywords"
              type="text"
              value={searchQuery}
              variant="outlined"
            />
          </Grid2>
          <Grid2
            size={{
              xs: 12,
              md: 3,
            }}
          >
            <TextField
              id="ResourceFilter"
              fullWidth
              label="Resource"
              onChange={(e) => setResource(e.target.value)}
              onKeyDown={handleEnterPress}
              placeholder="Filter by resource"
              type="text"
              value={resource}
              variant="outlined"
            />
          </Grid2>
          <Grid2
            size={{
              xs: 12,
              md: 3,
            }}
          >
            <TextField
              id="OperationFilter"
              fullWidth
              label="Operation"
              onChange={(e) => setOperation(e.target.value)}
              onKeyDown={handleEnterPress}
              placeholder="Filter by operation"
              type="text"
              value={operation}
              variant="outlined"
            />
          </Grid2>
        </Grid2>
        <Stack direction="row" gap={2} justifyContent="end">
          <Button onClick={clearFilters} variant="text">
            Clear all filters
          </Button>
          <Button onClick={filter} loading={isFetching} variant="contained">
            FILTER
          </Button>
        </Stack>
      </Stack>
      <ActivityLogGrid
        onPaginationModelChange={setPaginationModel}
        paginationModel={paginationModel}
        rows={activityLog}
        total={totalActivityLogCount}
        onSortChange={(field, direction) => {
          setSort(field);
          setSortDirection(direction);
        }}
        sort={sort}
        sortDirection={sortDirection}
      />
    </Stack>
  );
}
